import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Bell, CheckCircle2, Copy, Eye, FileText, FolderOpen, KeyRound, MoreHorizontal, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { frDate } from "@/lib/constants";
import { fmtDT } from "@/components/graphisme/gUtils";
import ClientFicheModal from "@/components/ClientFicheModal";
import { GraphismeAccessCard, GraphismeUsersCard } from "@/components/graphisme/ClientGraphisme";

const BLOCKED_LABELS = { graphiste: "Graphiste", client: "Client", paiement: "Paiement", personne : "Personne" };
const BLOCKED_CLS = { graphiste: "text-blue-700", client: "text-orange-700", paiement: "text-red-600", personne: "text-neutral-400" };

const SIMPLE_CLS = {
  brouillon: "bg-neutral-100 text-neutral-600 border-neutral-200",
  devis_en_attente: "bg-yellow-50 text-yellow-700 border-yellow-200",
  a_planifier: "bg-blue-50 text-blue-700 border-blue-200",
  en_cours: "bg-cyan-50 text-cyan-700 border-cyan-200",
  validation_client: "bg-orange-50 text-orange-700 border-orange-200",
  a_facturer: "bg-purple-50 text-purple-700 border-purple-200",
  attente_reglement: "bg-red-50 text-red-700 border-red-200",
  livraison: "bg-teal-50 text-teal-700 border-teal-200",
  finalise: "bg-green-50 text-green-700 border-green-200",
};

const STEPS = ["Devis", "Production", "Validation", "Paiement", "Livraison"];
const STEP_INDEX = { brouillon: 0, devis_en_attente: 0, a_planifier: 1, en_cours: 1,
  validation_client: 2, a_facturer: 3, attente_reglement: 3, livraison: 4, finalise: 4 };

function ProgressSteps({ simple }) {
  const idx = STEP_INDEX[simple] ?? 0;
  return (
    <div className="flex items-center gap-1 text-[10px] text-neutral-400" data-testid="gf-progress-steps">
      {STEPS.map((s, i) => (
        <span key={s} className="flex items-center gap-1">
          <span className={i < idx ? "text-green-600 font-medium" : i === idx ? "text-neutral-900 font-semibold" : ""}>
            {s} {i < idx ? "✓" : i === idx ? "●" : "○"}
          </span>
          {i < STEPS.length - 1 && <span className="text-neutral-200">→</span>}
        </span>
      ))}
    </div>
  );
}

function ProjectCard({ p, onOpen, onAction }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2.5" data-testid={`gf-projet-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold truncate">{p.name}</p>
          <p className="text-[11px] text-neutral-400 font-mono">{p.number}</p>
        </div>
        <span className={`text-[11px] rounded-full px-2.5 py-1 border whitespace-nowrap ${SIMPLE_CLS[p.simple_status]}`}>{p.simple_label}</span>
      </div>
      <ProgressSteps simple={p.simple_status} />
      <div className="text-xs text-neutral-500 flex flex-wrap gap-x-4 gap-y-0.5">
        <span>Progression : <strong>{p.progress.done} / {p.progress.total}</strong> créations validées</span>
        {p.deadline && <span>Deadline : <strong>{frDate(p.deadline)}</strong></span>}
      </div>
      {p.creations.length > 0 && (
        <div className="text-xs space-y-0.5" data-testid={`gf-creations-${p.id}`}>
          {p.creations.map((c) => (
            <p key={c.id} className="flex items-center gap-1.5 text-neutral-600">
              <FolderOpen className="w-3 h-3 text-neutral-300" />
              {c.name} — {c.status === "validee" ? <span className="text-green-600">✓ Validée</span>
                : c.status === "envoyee_validation" ? <span className="text-orange-600">⏳ En attente validation</span>
                : <span className="text-neutral-400">En cours</span>}
            </p>
          ))}
        </div>
      )}
      <div className="text-xs flex flex-wrap gap-x-4 gap-y-0.5 pt-1 border-t border-neutral-100">
        <span>Bloqué par : <strong className={BLOCKED_CLS[p.blocked_by]}>{BLOCKED_LABELS[p.blocked_by]}</strong></span>
        <span>Prochaine action : <strong>{p.next_action}</strong></span>
      </div>
      <div className="flex gap-2 pt-1">
        {p.action_kind === "planifier" && (
          <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" data-testid={`gf-action-${p.id}`}
            onClick={() => onAction("planifier", p)}>Planifier</Button>
        )}
        {p.action_kind === "relancer" && (
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`gf-action-${p.id}`}
            onClick={() => onAction("relancer", p)}>Relancer</Button>
        )}
        {p.action_kind === "devis" && (
          <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" data-testid={`gf-action-${p.id}`}
            onClick={() => onAction("devis", p)}>{p.quote_id ? "Voir le devis" : "Créer le devis"}</Button>
        )}
        {p.action_kind === "facturer" && (
          <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" data-testid={`gf-action-${p.id}`}
            onClick={() => onAction("facturer", p)}>Facturer</Button>
        )}
        {p.action_kind === "livrer" && (
          <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" data-testid={`gf-action-${p.id}`}
            onClick={() => onAction("livrer", p)}>Préparer / livrer</Button>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`gf-open-${p.id}`}
          onClick={() => onOpen(p)}>Ouvrir</Button>
      </div>
    </div>
  );
}

export default function GraphismeClientFiche({ client: base }) {
  const navigate = useNavigate();
  const [fiche, setFiche] = useState(null);
  const [showFinalises, setShowFinalises] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ficheModal, setFicheModal] = useState(false);
  const [fullClient, setFullClient] = useState(base);

  const load = () => api.get(`/clients/${base.id}/graphisme-fiche`).then((r) => setFiche(r.data)).catch(() => {});
  useEffect(() => { load(); }, [base.id]);

  const open = (p) => navigate(`/graphisme/projet/${p.id}`);
  const cardAction = async (kind, p) => {
    if (kind === "planifier") navigate(`/graphisme/projet/${p.id}?planifier=1`);
    else if (kind === "devis") {
      if (p.quote_id) navigate(`/comptabilite?tab=devis&quote=${p.quote_id}`);
      else { const r = await api.post(`/graphisme/projects/${p.id}/quote`, {}); navigate(`/comptabilite?tab=devis&quote=${r.data.id}`); }
    } else if (kind === "facturer") navigate(`/graphisme/projet/${p.id}`);
    else if (kind === "livrer") navigate(`/graphisme/projet/${p.id}`);
    else if (kind === "relancer") { open(p); toast.info("Ouvrez le projet pour relancer le client"); }
  };

  if (!fiche) return (
    <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
  );
  const c = fiche.client;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="graphisme-client-fiche">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-3" data-testid="gf-client-name">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shrink-0" style={{ background: c.color || "#1a1a1a" }}>
              {c.name?.slice(0, 1)}
            </span>
            {c.name}
          </h1>
          <div className="text-sm text-neutral-500 mt-1.5 space-y-0.5">
            {(c.contact_first_name || c.contact_last_name) && (
              <p>Contact : {[c.contact_first_name, c.contact_last_name].filter(Boolean).join(" ")}</p>
            )}
            {c.email && <p>{c.email}</p>}
            {c.phone && <p>{c.phone}</p>}
            <p>
              Espace client : {fiche.access.enabled
                ? <span className="text-green-600 font-medium" data-testid="gf-access-status">🟢 Actif</span>
                : <span className="text-neutral-400" data-testid="gf-access-status">Inactif</span>}
              <span className="text-neutral-300 mx-2">·</span>
              {fiche.counts.actifs} projet(s) actif(s)
              <span className="text-neutral-300 mx-2">·</span>
              {fiche.counts.actions} action(s) en attente
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="gf-preview-client"
            onClick={() => navigate(`/graphisme/apercu/${base.id}`)}>
            <Eye className="w-3.5 h-3.5" /> Voir comme le client
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="gf-copy-link"
            onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/connexion`); toast.success("Lien de connexion client copié"); }}>
            <Copy className="w-3.5 h-3.5" /> Copier le lien
          </Button>
          <div className="relative">
            <Button size="sm" variant="outline" className="h-8 w-8 p-0" data-testid="gf-menu"
              onClick={() => setMenuOpen((v) => !v)}><MoreHorizontal className="w-4 h-4" /></Button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-9 z-40 bg-white border border-neutral-200 rounded-xl shadow-lg py-1 w-52" data-testid="gf-menu-list">
                  <button className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50" data-testid="gf-menu-infos"
                    onClick={async () => { setMenuOpen(false); const r = await api.get(`/clients/${base.id}`); setFullClient(r.data); setFicheModal(true); }}>
                    <FileText className="w-3.5 h-3.5 text-neutral-400" /> Informations
                  </button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50" data-testid="gf-menu-facturation"
                    onClick={() => navigate(`/comptabilite?client=${base.id}`)}>
                    <FileText className="w-3.5 h-3.5 text-neutral-400" /> Facturation
                  </button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50" data-testid="gf-menu-historique"
                    onClick={() => { setMenuOpen(false); document.getElementById("gf-historique")?.scrollIntoView({ behavior: "smooth" }); }}>
                    <Archive className="w-3.5 h-3.5 text-neutral-400" /> Historique
                  </button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-red-600 hover:bg-red-50" data-testid="gf-menu-archive"
                    onClick={async () => {
                      setMenuOpen(false);
                      if (!window.confirm(`Archiver le client « ${c.name} » ? Il n'apparaîtra plus dans la liste active.`)) return;
                      try {
                        await api.delete(`/clients/${base.id}`);
                        toast.success("Client archivé");
                        window.dispatchEvent(new Event("sf:clients"));
                        navigate("/clients");
                      } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de l'archivage"); }
                    }}>
                    <Archive className="w-3.5 h-3.5" /> Archiver le client
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {fiche.a_traiter.length > 0 && (
        <div className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-4 space-y-1.5" data-testid="gf-a-traiter">
          <p className="font-heading font-bold text-orange-900 flex items-center gap-2 text-sm">
            <Zap className="w-4 h-4" /> À traiter — {fiche.a_traiter.length}
          </p>
          {fiche.a_traiter.map((a, i) => (
            <button key={i} onClick={() => navigate(a.url)} data-testid={`gf-todo-${i}`}
              className="w-full flex items-center gap-2 text-sm text-orange-800 bg-white/60 border border-orange-200 rounded-lg px-3 py-2 text-left hover:border-orange-400 transition-colors">
              {a.kind === "demande" ? <Bell className="w-4 h-4 shrink-0" /> : a.kind === "facture" ? <FileText className="w-4 h-4 shrink-0" /> : <CheckCircle2 className="w-4 h-4 shrink-0" />}
              {a.text} <span className="ml-auto text-xs underline shrink-0">Agir →</span>
            </button>
          ))}
        </div>
      )}

      <section>
        <h2 className="font-heading font-bold text-lg mb-3">Projets en cours <span className="text-sm font-normal text-neutral-400">({fiche.projets_en_cours.length})</span></h2>
        {fiche.projets_en_cours.length === 0 ? (
          <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-200 rounded-xl p-6 text-center" data-testid="gf-no-projects">
            Aucun projet en cours.
          </p>
        ) : (
          <div className="space-y-3">
            {fiche.projets_en_cours.map((p) => <ProjectCard key={p.id} p={p} onOpen={open} onAction={cardAction} />)}
          </div>
        )}
      </section>

      {fiche.demandes.length > 0 && (
        <section data-testid="gf-demandes">
          <h2 className="font-heading font-bold text-lg mb-3">
            Nouvelles demandes <span className="inline-flex items-center justify-center bg-red-500 text-white text-xs rounded-full min-w-5 h-5 px-1.5 ml-1">{fiche.demandes.length}</span>
          </h2>
          <div className="space-y-2">
            {fiche.demandes.map((d) => (
              <div key={d.id} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`gf-demande-${d.id}`}>
                <div>
                  <p className="font-semibold text-sm">{d.name}</p>
                  <p className="text-xs text-neutral-400">Reçue le {fmtDT(d.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`gf-demande-voir-${d.id}`} onClick={() => navigate(`/graphisme/projet/${d.id}`)}>Voir</Button>
                  <Button size="sm" className="h-8 text-xs bg-neutral-900 hover:bg-neutral-800 text-white" data-testid={`gf-demande-create-${d.id}`} onClick={() => navigate(`/graphisme/projet/${d.id}`)}>Créer le projet</Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <button className="font-heading font-bold text-lg mb-3 flex items-center gap-2" data-testid="gf-finalises-toggle"
          onClick={() => setShowFinalises((v) => !v)}>
          Projets finalisés ({fiche.counts.finalises}) <span className="text-sm text-neutral-400">{showFinalises ? "▲" : "▼"}</span>
        </button>
        {showFinalises && (
          <div className="space-y-2">
            {fiche.finalises.map((p) => (
              <div key={p.id} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`gf-finalise-${p.id}`}>
                <div>
                  <p className="font-semibold text-sm">{p.name}</p>
                  <p className="text-xs text-green-600">✓ Finalisé</p>
                </div>
                <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`gf-finalise-open-${p.id}`} onClick={() => open(p)}>Voir le projet</Button>
              </div>
            ))}
            {fiche.finalises.length === 0 && <p className="text-sm text-neutral-400">Aucun projet finalisé.</p>}
          </div>
        )}
      </section>

      <section className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gf-acces">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2"><KeyRound className="w-4 h-4" /> Accès & utilisateurs</h2>
        <p className="text-xs text-neutral-400 mt-1">Créez l'espace client unique (identifiant + mot de passe), choisissez les plateformes accessibles et gérez les utilisateurs additionnels.</p>
        <GraphismeAccessCard clientId={base.id} />
        <GraphismeUsersCard clientId={base.id} />
      </section>

      <section id="gf-historique" className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="gf-historique">
        <h2 className="font-heading font-bold text-lg mb-3">Historique</h2>
        {fiche.activity.length === 0 ? (
          <p className="text-sm text-neutral-400">Aucune activité récente.</p>
        ) : (
          <div className="space-y-2">
            {fiche.activity.map((a, i) => (
              <div key={i} className="text-xs flex gap-2 items-baseline">
                <span className="text-neutral-400 whitespace-nowrap">{fmtDT(a.at)}</span>
                <span className="text-neutral-500 shrink-0">[{a.project_name}]</span>
                <span>{a.text}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <ClientFicheModal open={ficheModal} onClose={() => setFicheModal(false)} client={fullClient}
        onSaved={() => { setFicheModal(false); load(); }} />
    </div>
  );
}
